import { InteractionState } from "../../Tiles/TileContext";
import {
	DimensionToRender,
	TileData,
	TileGridConfiguration,
	TileRenderState
} from "../common";
import { GridDimension, GridPosition } from "client/Api/Workspace/common";
import { getPossibleDimensions } from "client/Features/Workspace/appTypeHelper";
import { sortBy } from "client_legacy/ndaw/arrayHelpers";
import { setPropertiesPreservingRefWhenUnchanged } from "./common";
import { deepComparison } from "client/Misc/compareUtils";
import * as tileSelectors from "../selectors/tile";

export function generateInitialState(
	tile: TileData,
	interactionState: InteractionState,
	configuration: TileGridConfiguration
): TileRenderState {
	const renderStateWithoutVisibility: PartlyPartial<TileRenderState, "isVisible"> = {
		data: tile,
		dimension: {
			kind: "normal",
			dimension: tile.dimension
		},
		position: tile.position,
		interactionState,
		isBeingDragged: false
	};

	const renderState: TileRenderState = {
		...renderStateWithoutVisibility,
		isVisible: calculateIsVisible(renderStateWithoutVisibility)
	};

	return applyTileGridConfiguration(
		renderState,
		configuration
	);
}

export function applyNewInteractionState(
	currentState: TileRenderState,
	interactionState: InteractionState
): TileRenderState {
	currentState = setPropertiesPreservingRefWhenUnchanged(
		currentState,
		{
			interactionState
		}
	);

	return setPropertiesPreservingRefWhenUnchanged(
		currentState,
		{
			isVisible: calculateIsVisible(currentState)
		}
	);
}

export function adjustDimensionForNewConfiguration(
	currentState: TileRenderState,
	configuration: TileGridConfiguration
): TileRenderState {
	const columnLimit: number = configuration.dimensionLimits
		.columns;

	const currentColumnSpan: number = currentState.data
		.dimension
		.columnSpan;

	if (columnLimit < currentColumnSpan) {
		currentState = {
			...currentState,
			data: {
				...currentState.data,
				dimension: getBestTileDimensionWithinColumnLimit(
					currentState,
					columnLimit
				)
			}
		};
	}

	currentState = applyTileGridConfiguration(currentState, configuration);

	return currentState;
}

export function applyTileGridConfiguration(
	currentState: TileRenderState,
	tileGridConfiguration: TileGridConfiguration
): TileRenderState {
	const {
		dimensionLimits: { columns: columnLimit },
		renderLiveTilesFullWidth
	} = tileGridConfiguration;

	const {
		dimension: {
			columnSpan: configuredColumnSpan,
			rowSpan: configuredRowSpan
		}
	} = currentState.data;

	const isLiveTile: boolean = configuredColumnSpan >= 4;
	const isTileWiderThanDimensionLimit: boolean = configuredColumnSpan > columnLimit;

	let dimensionToRender: DimensionToRender;

	if (isLiveTile && renderLiveTilesFullWidth) {
		dimensionToRender = {
			kind: "responsive",
			dimension: {
				columnSpan: columnLimit,
				rowSpan: configuredRowSpan >= 4
					? Math.max(configuredRowSpan, columnLimit)
					: configuredRowSpan
			}
		};
	} else if (isTileWiderThanDimensionLimit) {
		dimensionToRender = {
			kind: "normal",
			dimension: getBestTileDimensionWithinColumnLimit(
				currentState,
				columnLimit
			)
		};
	} else {
		dimensionToRender = {
			kind: "normal",
			dimension: currentState.data
				.dimension
		};
	}

	const areTheSame: boolean = deepComparison(currentState.dimension, dimensionToRender);

	return areTheSame
		? currentState
		: {
			...currentState,
			dimension: dimensionToRender
		};
}

export function setIsBeingDragged(
	currentState: TileRenderState,
	isBeingDragged: boolean
): TileRenderState {
	return setPropertiesPreservingRefWhenUnchanged(
		currentState,
		{
			isBeingDragged
		}
	);
}

export function setRenderPosition(currentState: TileRenderState, position: GridPosition): TileRenderState {
	const isTheSame: boolean = deepComparison(
		currentState.position,
		position
	);

	if (isTheSame) {
		return currentState;
	}

	return {
		...currentState,
		position
	};
}

export function persistRenderPosition(
	currentState: TileRenderState
): TileRenderState {
	const renderPosition: GridPosition = currentState.position;

	const isTheSame: boolean = deepComparison(
		currentState.data
			.position,
		renderPosition
	);

	if (isTheSame) {
		return currentState;
	}

	return {
		...currentState,
		data: {
			...currentState.data,
			position: renderPosition
		}
	};
}

export function setRenderDimension(currentState: TileRenderState, dimension: GridDimension): TileRenderState {
	const isTheSame: boolean = deepComparison(
		currentState.dimension
			.dimension,
		dimension
	);

	if (isTheSame) {
		return currentState;
	}

	return {
		...currentState,
		dimension: {
			...currentState.dimension,
			dimension
		}
	};
}

export function persistRenderDimension(
	currentState: TileRenderState
): TileRenderState {
	const { dimension } = currentState;
	if (dimension.kind !== "normal") {
		throw new Error("Cannot persist a tile dimension that is not normal");
	}

	const renderDimension: GridDimension = dimension
		.dimension;

	const isTheSame: boolean = deepComparison(
		currentState.data
			.dimension,
		renderDimension
	);

	if (isTheSame) {
		return currentState;
	}

	return {
		...currentState,
		data: {
			...currentState.data,
			dimension: renderDimension
		}
	};
}

function calculateIsVisible(
	tile: Omit<TileRenderState, "isVisible">
): boolean {
	if (tileSelectors.isHiddenByConditionalAccess(tile)) {
		return false;
	}

	const {
		data,
		interactionState
	} = tile;

	const isHidden: boolean = (data.appIsNotAccessible || !data.appIsLicensed)
		&& interactionState === InteractionState.Default;

	return !isHidden;
}

function getBestTileDimensionWithinColumnLimit(
	currentState: TileRenderState,
	columnLimit: number
): GridDimension {
	const tile: TileData = currentState.data;
	const allDimensions: readonly GridDimension[] = getPossibleDimensions(tile.type);
	const rowLimit: number = tile.dimension
		.rowSpan;

	const withinLimit: GridDimension[] = sortBy(
		sortBy(
			allDimensions.filter(
				d => d.columnSpan <= columnLimit
					&& d.rowSpan <= rowLimit
			),
			d => d.rowSpan
		),
		d => d.columnSpan
	);

	if (withinLimit.length === 0) {
		throw new Error(`Could not find suitable tile dimension for app type ${tile.type} within limit ${columnLimit}x${rowLimit}`);
	}

	return withinLimit[withinLimit.length - 1];
}